<template>
  <section class="report-chapter risk-assessment-chapter">
    <ReportChapterTitle :title="chapterTitle || '二 风险评估'" />

    <ReportRibbon title="（一）易患病症" />
    <div class="risk-summary" v-if="riskGroups.length">
      <div class="risk-summary-head">
        <div v-for="group in riskGroups" :key="group.key" class="risk-column-title">
          {{ group.title }}（{{ group.items.length }}项）
        </div>
      </div>
      <div class="risk-list">
        <div v-for="(row, rowIndex) in riskRows" :key="rowIndex" class="risk-item-row">
          <div
            v-for="(item, index) in row"
            :key="item.name + '-' + rowIndex + '-' + index"
            class="risk-item"
          >
            <span class="risk-name">{{ item.name }}</span>
            <span
              v-if="isValidScore(item)"
              class="risk-score"
              :class="riskClass(item)"
              :style="riskColorStyle(item)"
            >
              {{ item.score }}
            </span>
            <span
              v-if="item.riskName"
              class="risk-level"
              :class="riskClass(item)"
              :style="riskColorStyle(item)"
            >
              {{ item.riskName }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="interpret-section" v-if="riskList.length">
      <ReportRibbon title="（二）异常解读" />
      <div v-for="(item, index) in riskList" :key="item.name + '-' + index" class="risk-detail">
        <div class="risk-detail-overview">
          <div class="risk-detail-head">
            <span v-if="item.name">{{ item.name }}</span>
            <strong
              v-if="item.riskName"
              :class="riskClass(item)"
              :style="riskColorStyle(item)"
            >
              <span>{{ item.riskName }}</span>
            </strong>
          </div>

          <div class="risk-scale" v-if="isValidScore(item)">
            <div class="scale-title">风险等级</div>
            <div class="scale-wrap">
              <div class="scale-bar">
                <span class="seg seg-green"></span>
                <span class="seg seg-yellow"></span>
                <span class="seg seg-orange"></span>
                <span class="seg seg-red"></span>
                <span class="scale-marker" :style="markerStyle(item)">
                  <i>{{ item.score }}分</i>
                </span>
              </div>
              <div class="scale-labels">
                <span>0</span>
                <span>25</span>
                <span>50</span>
                <span>75</span>
                <span>100</span>
              </div>
            </div>
          </div>
        </div>

        <div class="interpret-block" data-report-text-block v-if="item.definition">
          <div class="interpret-title">风险定义</div>
          <p>{{ item.definition }}</p>
        </div>
        <div class="interpret-block" data-report-text-block v-if="item.aiInterpret">
          <div class="interpret-title">AI智能分析</div>
          <p>{{ item.aiInterpret }}</p>
        </div>
        <div class="interpret-block" data-report-text-block v-if="item.healthSuggestion">
          <div class="interpret-title">健康建议</div>
          <p>{{ item.healthSuggestion }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import ReportRibbon from './ReportRibbon';
import ReportChapterTitle from './ReportChapterTitle';
import {
  clampScore,
  hasValueDeep,
  isValidValue,
  toArray,
} from '../helpers';

export default {
  name: 'RiskAssessment',
  components: {
    ReportRibbon,
    ReportChapterTitle,
  },
  props: {
    report: {
      type: Object,
      default: () => ({}),
    },
    chapterTitle: {
      type: String,
      default: '',
    },
  },
  computed: {
    riskList() {
      return toArray(this.report.riskReport && this.report.riskReport.riskList)
        .filter((item) => hasValueDeep(item))
        .map((item) => ({
          ...item,
          name: item.name || '',
          score: isValidValue(item.score) ? item.score : '',
          riskName: item.riskName || '',
        }))
        .filter((item) => item.name || item.riskName || this.isValidScore(item));
    },
    riskGroups() {
      const groups = [
        { key: 'high', title: '重度风险', items: [] },
        { key: 'medium', title: '中度风险', items: [] },
        { key: 'light', title: '轻度风险', items: [] },
      ];
      this.riskList.forEach((item) => {
        const key = this.getRiskType(item);
        const group = groups.find((groupItem) => groupItem.key === key);
        if (group) group.items.push(item);
      });
      return groups;
    },
    riskRows() {
      const rows = [];
      for (let index = 0; index < this.riskList.length; index += 2) {
        rows.push(this.riskList.slice(index, index + 2));
      }
      return rows;
    },
  },
  methods: {
    getRiskType(item) {
      const riskName = String(item.riskName || '');
      const riskLevel = Number(item.riskLevel);
      if (riskName.includes('正常')) return 'normal';
      if (riskName.includes('高') || riskName.includes('严重')) return 'high';
      if (riskName.includes('中')) return 'medium';
      if (
        riskName.includes('轻')
        || riskName.includes('低')
      ) return 'light';
      if (riskLevel === 4) return 'high';
      if (riskLevel === 3) return 'medium';
      if (riskLevel === 2) return 'light';
      if (riskLevel === 1) return 'normal';
      return 'light';
    },
    riskClass(item) {
      const type = this.getRiskType(item);
      if (type === 'high') return 'is-high';
      if (type === 'medium') return 'is-medium';
      if (type === 'normal') return 'is-normal';
      return 'is-light';
    },
    isValidScore(item) {
      return isValidValue(item && item.score);
    },
    markerStyle(item) {
      return {
        left: String(clampScore(item.score)).concat('%'),
        '--scale-marker-color': this.scaleMarkerColor(item),
      };
    },
    scaleMarkerColor(item) {
      const score = clampScore(item.score);
      if (score < 25) return '#13c88a';
      if (score < 50) return '#ffc412';
      if (score < 75) return '#ff751f';
      return '#f82550';
    },
    riskColorStyle(item) {
      if (!this.isValidScore(item)) return null;
      return {
        color: this.scaleMarkerColor(item),
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.risk-summary {
  margin-top: 0;
  border-bottom: 1px solid #9e6c41;
}

.risk-summary-head {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  padding: 0 8px;
  background: rgba(224, 190, 132, 0.22);
}

.risk-column-title {
  padding: 7px 4px;
  color: #68311d;
  font-size: 10px;
  line-height: 16px;
  font-weight: 700;
  text-align: center;
}

.risk-list {
  padding: 6px 8px 8px;
}

.risk-item-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 8px;
}

.risk-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) max-content max-content;
  column-gap: 3px;
  align-items: center;
  min-height: 24px;
  color: #68311d;
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
}

.risk-name {
  overflow: hidden;
  white-space: nowrap;
}

.risk-score,
.risk-level {
  text-align: center;
  font-weight: 700;
  white-space: nowrap;
}

.is-high {
  color: #f5385a;
}

.is-medium {
  color: #ff7a1a;
}

.is-light {
  color: #5fc263;
}

.is-normal {
  color: #5fc263;
}

.interpret-section {
  margin-top: 34px;
  padding-top: 1px;
}

.risk-detail {
  padding: 0 3px 17px;
  margin-bottom: 14px;
  border-bottom: 1px solid #9e6c41;
}

.risk-detail-overview {
  display: inline-block;
  width: 100%;
  box-sizing: border-box;
  vertical-align: top;
  break-inside: avoid-column;
  page-break-inside: avoid;
  -webkit-column-break-inside: avoid;
}

.risk-detail-head {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  column-gap: 8px;
  min-height: 30px;
  padding: 0 12px;
  background: rgba(224, 190, 132, 0.22);
  color: #68311d;
  font-size: 14px;
  line-height: 21px;

  span {
    font-weight: 600;
  }

  strong {
    margin-left: auto;
    font-size: 14px;
  }
}

.scale-title,
.interpret-title {
  margin: 10px 0 8px;
  color: #68311d;
  font-size: 15px;
  line-height: 21px;
  font-weight: 700;
}

.risk-scale {
  display: inline-block;
  width: 100%;
  box-sizing: border-box;
  vertical-align: top;
  break-inside: avoid-column;
  page-break-inside: avoid;
  -webkit-column-break-inside: avoid;
}

.scale-wrap {
  display: inline-block;
  width: 100%;
  box-sizing: border-box;
  padding: 22px 0 16px;
  vertical-align: top;
  break-inside: avoid-column;
  page-break-inside: avoid;
  -webkit-column-break-inside: avoid;
}

.scale-bar {
  position: relative;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  height: 9px;
  overflow: visible;
  border-radius: 999px;
}

.seg {
  display: block;
  height: 9px;
}

.seg-green {
  border-radius: 999px 0 0 999px;
  background: #13c88a;
}

.seg-yellow {
  background: #ffc412;
}

.seg-orange {
  background: #ff751f;
}

.seg-red {
  border-radius: 0 999px 999px 0;
  background: #f82550;
}

.scale-marker {
  position: absolute;
  top: -13px;
  width: 10px;
  height: 13px;
  margin-left: -5px;

  &::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 0;
    width: 8px;
    height: 13px;
    transform: translateX(-50%);
    background: var(--scale-marker-color, #8f9398);
    clip-path: polygon(18% 0, 82% 0, 82% 68%, 100% 68%, 50% 100%, 0 68%, 18% 68%);
  }

  i {
    position: absolute;
    left: 50%;
    top: -15px;
    transform: translateX(-50%);
    color: var(--scale-marker-color, #f0a23b);
    font-size: 10px;
    line-height: 12px;
    font-style: normal;
    font-weight: 700;
    white-space: nowrap;
  }
}

.scale-labels {
  display: flex;
  justify-content: space-between;
  margin-top: 7px;
  color: #68311d;
  font-size: 10px;
  line-height: 12px;
}

.interpret-block {
  margin-top: 14px;

  p {
    margin: 0;
    color: #68311d;
    font-size: 14px;
    line-height: 25px;
    text-align: justify;
  }
}
</style>
